import { HttpStatusCodes } from '../Constants/HttpStatusCodes';
import { User, UserType } from '../Types/User';
import {
  generateAssetRenditionUrl,
  ImageFormat,
  PROFILE_IMAGE_RENDITIONS,
} from '../Utils/Assets';
import { createApiInstance, getApiInstance } from './Api';

export interface SignUpInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  grade: string;
  type: UserType;
}

export interface SignInOutput {
  status: 'ok' | 'payment_required';
  user: User;
  userAuthorizationInfo: UserAuthorizationInfo;
}

export interface CognitoCredentials {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface SignUpAsAnonymousOutput {
  email: string;
  password: string;
}

export interface UserAuthorizationInfo {
  canAccessParentUI: boolean;
  canAccessStudentUI: boolean;
  canStartTrial: boolean;
}

export const mapUser = (user: any): User => {
  const profileImageUrl =
    user.profileImagePath &&
    new URL(
      user.profileImagePath,
      window.env.REACT_APP_MEDIA_BASE_URL,
    ).toString();

  // This is to avoid 'parent.children.parent.children...' queries on the backend
  // We'd do this in the backend UserViewModel, but it considers it an attempt to convert a circular structure to json
  const children = user.children?.map(mapUser);
  if (children) {
    for (const child of children) {
      child.parent = user;
    }
  }

  return {
    ...user,
    profileImage: {
      originalUrl: profileImageUrl,
      compressedUrl:
        profileImageUrl &&
        generateAssetRenditionUrl(profileImageUrl, null, ImageFormat.WEBP),
      iconUrl:
        profileImageUrl &&
        generateAssetRenditionUrl(
          profileImageUrl,
          PROFILE_IMAGE_RENDITIONS.ICON,
          ImageFormat.WEBP,
        ),
    },
    parent: user.parent && mapUser(user.parent),
    children: children,
    createdAt: user.createdAt && new Date(Date.parse(user.createdAt)),
  };
};

export class UserApi {
  async getUser(userId: string): Promise<User> {
    try {
      const response = await getApiInstance().get(`/users/${userId}`);
      const user = response.data;
      console.log('Fetched user: ', user);

      return mapUser(user);
    } catch (error) {
      console.error('Error fetching user', error);
      throw error;
    }
  }

  /**
   * Updates the user's information.
   *
   * @param user The user object containing updated information.
   * @returns A promise that resolves with the updated user object.
   * @throws An error if the update operation fails.
   */
  async updateUser(user: User): Promise<User> {
    try {
      const response = await getApiInstance().patch(`/users/${user.id}`, user);
      const updatedUser: User = response.data;
      console.log('User updated: ', updatedUser);
      return mapUser(updatedUser);
    } catch (error) {
      console.error('Error updating user', error);
      throw error;
    }
  }

  /**
   * Updates the profile image of a user.
   *
   * @param userId The ID of the user whose profile image is to be updated.
   * @param image The new profile image file to be uploaded.
   * @returns A promise that resolves with no value upon successful update.
   * @throws An error if the update operation fails.
   */
  async updateProfileImage(userId: string, image: File): Promise<void> {
    try {
      const formData = new FormData();
      formData.append('image', image);
      await getApiInstance().put(`/users/${userId}/profile-image`, formData);
    } catch (error) {
      console.error('Error updating profile image', error);
      throw error;
    }
  }

  async signUp(input: SignUpInput): Promise<User> {
    try {
      const response = await getApiInstance().post('/auth/signup', input);
      const user: User = response.data;
      console.log('User added: ', user);
      return mapUser(user);
    } catch (error) {
      if (error?.response?.status === HttpStatusCodes.Conflict) {
        console.warn('Error signing up user. User already exists.', error);
      } else {
        console.error('Error signing up user', error);
      }
      throw error;
    }
  }

  async signUpAsAnonymous(): Promise<SignUpAsAnonymousOutput> {
    try {
      const response = await getApiInstance().post(
        '/auth/signup/anonymous',
        {},
      );
      return response.data;
    } catch (error) {
      console.error('Error signing up anonymous user', error);
      throw error;
    }
  }

  /**
   * Verifies if the user exists in the reading app and creates it if not
   *
   * @param userId userId to check if exists in reading app
   */
  async checkUserExistsInReadingAppAndCreateIfNot(): Promise<SignInOutput> {
    console.log(`Checking if user exists in reading app`);
    try {
      const response = await createApiInstance({
        validateStatus: (status) => {
          // Otherwise, Axios will fail on 402
          return [200, 201, 402].includes(status);
        },
      }).post(`/auth/signin/`);
      console.debug('User exists in reading app (?)', response.data);
      switch (response.status) {
        case 200:
          console.log('User exists, nothing more to do');
          return {
            status: 'ok',
            user: response.data.user,
            userAuthorizationInfo: response.data.userAuthorizationInfo,
          };
        default:
          console.error(
            'Unexpected response',
            response.status,
            response.data,
            JSON.stringify(response, null, 2),
          );
          throw new Error('Cannot proceed');
      }
    } catch (error) {
      console.error('Error checking user exists in reading app', error);
      throw error;
    } finally {
      console.log('Checked if user exists in reading app');
    }
  }

  async authenticateWithMagicToken(token: string): Promise<CognitoCredentials> {
    try {
      const response = await getApiInstance().post(
        '/auth/authenticate/magic-token',
        {
          token,
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error signing in with magic token', error);
      throw error;
    }
  }

  /**
   * Deletes the user's account.
   *
   * @param userId The ID of the user whose account is to be deleted.
   * @returns A promise that resolves with no value upon successful deletion.
   * @throws An error if the deletion operation fails.
   */
  async deleteUser(userId: string): Promise<void> {
    try {
      await getApiInstance().delete(`/users/${userId}`);
    } catch (error) {
      console.error('Error deleting account', error);
      throw error;
    }
  }

  async getUserAuthorizationInfo(): Promise<UserAuthorizationInfo> {
    try {
      const response = await getApiInstance().get('/auth/authorization');
      return response.data;
    } catch (error) {
      console.error('Error checking if user is enrolled in Premium', error);
      throw error;
    }
  }

  async startTrial(): Promise<UserAuthorizationInfo> {
    try {
      const response = await getApiInstance().post('/auth/trial');
      return response.data;
    } catch (error) {
      console.error('Error starting trial', error);
      throw error;
    }
  }

  async redeemVoucher(voucherCode: string): Promise<UserAuthorizationInfo> {
    try {
      const response = await getApiInstance().post('/auth/redeem-voucher', {
        voucherCode,
      });
      return response.data;
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      } else {
        throw error;
      }
    }
  }

  /**
   * Updates the device token for a user.
   *
   * @param userId The ID of the user whose device token is to be updated.
   * @param deviceToken The new mobile device token to be updated.
   * @returns A promise that resolves with no value upon successful update.
   * @throws An error if the update operation fails.
   */
  async saveDeviceToken(deviceToken: string): Promise<void> {
    try {
      await getApiInstance().put(`/user-device/register`, {
        deviceToken,
      });
    } catch (error) {
      console.error('Error updating device token', error);
      throw error;
    }
  }
}

export default UserApi;
